import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  ChartColumn,
  CircleDollarSign,
  CreditCard,
  Settings2,
  type LucideIcon,
} from 'lucide-react';
import { KPIData } from '../services/kpiService';

const COLOR_BLUE = '#2563eb';
const COLOR_CYAN = '#0891b2';
const COLOR_EMERALD = '#059669';
const COLOR_AMBER = '#d97706';
const COLOR_VIOLET = '#7c3aed';
const COLOR_ROSE = '#e11d48';
const AXIS_TICK_STYLE = { fill: '#64748b', fontSize: 12 };
const GRID_STROKE = '#e2e8f0';
const TOOLTIP_STYLE = {
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  boxShadow: '0 10px 30px -12px rgba(15, 23, 42, 0.24)',
  color: '#0f172a',
  fontSize: 13,
};

interface ChartCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  iconClassName: string;
  iconBackgroundClassName: string;
  children: React.ReactNode;
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  description,
  icon: Icon,
  iconClassName,
  iconBackgroundClassName,
  children,
}) => (
  <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconBackgroundClassName}`}>
        <Icon className={`h-5 w-5 ${iconClassName}`} aria-hidden="true" />
      </span>
      <div>
        <h3 className="text-base font-semibold text-slate-950">{title}</h3>
        <p className="mt-0.5 text-xs leading-5 text-slate-500">{description}</p>
      </div>
    </div>
    <div className="px-2 pb-4 pt-5 sm:px-4">{children}</div>
  </article>
);

const EmptyChart: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex h-[300px] items-center justify-center px-6 text-center">
    <div>
      <ChartColumn className="mx-auto h-8 w-8 text-slate-300" aria-hidden="true" />
      <p className="mt-3 text-sm font-medium text-slate-700">{message}</p>
      <p className="mt-1 text-xs text-slate-500">Data will appear when activity is recorded.</p>
    </div>
  </div>
);

interface DashboardChartsProps {
  kpiData: KPIData;
  formatCurrency: (amount: string | number) => string;
}

export const DashboardCharts: React.FC<DashboardChartsProps> = ({ kpiData, formatCurrency }) => {
  const registrationsData = [
    {
      period: 'Today',
      'Service Providers': kpiData.userMetrics.serviceProviders.newRegistrations.today,
      'Service Users': kpiData.userMetrics.serviceUsers.newRegistrations.today,
    },
    {
      period: 'This Week',
      'Service Providers': kpiData.userMetrics.serviceProviders.newRegistrations.thisWeek,
      'Service Users': kpiData.userMetrics.serviceUsers.newRegistrations.thisWeek,
    },
    {
      period: 'This Month',
      'Service Providers': kpiData.userMetrics.serviceProviders.newRegistrations.thisMonth,
      'Service Users': kpiData.userMetrics.serviceUsers.newRegistrations.thisMonth,
    },
  ];

  const revenueData = [
    { period: 'Today', revenue: Number.parseFloat(kpiData.revenueAndPaymentMetrics.revenueByPeriod.today || '0') },
    { period: 'This Week', revenue: Number.parseFloat(kpiData.revenueAndPaymentMetrics.revenueByPeriod.thisWeek || '0') },
    { period: 'This Month', revenue: Number.parseFloat(kpiData.revenueAndPaymentMetrics.revenueByPeriod.thisMonth || '0') },
  ];

  const paymentStatusLabels: Record<string, string> = {
    completed: 'Completed',
    pending: 'Pending',
    not_required: 'Not required',
    expired: 'Expired',
    required_after_first_service: 'Required after service',
  };
  const paymentStatusColors = [COLOR_EMERALD, COLOR_AMBER, '#94a3b8', COLOR_ROSE, COLOR_BLUE];
  const paymentStatusData = Object.entries(kpiData.revenueAndPaymentMetrics.paymentStatusBreakdown).map(
    ([key, value], index) => ({
      status: paymentStatusLabels[key] || key,
      count: value || 0,
      fill: paymentStatusColors[index % paymentStatusColors.length],
    })
  );

  const providerStatusLabels: Record<string, string> = {
    active: 'Active',
    pending_verification: 'Pending verification',
    profile_completed: 'Profile completed',
    contact_verified: 'Contact verified',
    documents_uploaded: 'Documents uploaded',
  };
  const providerStatusColors = [COLOR_BLUE, COLOR_AMBER, COLOR_CYAN, COLOR_VIOLET, COLOR_ROSE];
  const providerStatusData = Object.entries(kpiData.userMetrics.serviceProviders.byStatus)
    .filter(([, value]) => (value || 0) > 0)
    .map(([key, value], index) => ({
      status: providerStatusLabels[key] || key,
      count: value || 0,
      fill: providerStatusColors[index % providerStatusColors.length],
    }));

  return (
    <section className="space-y-4" aria-labelledby="dashboard-analytics-title">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">Analytics</p>
        <div className="mt-1 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          <h2 id="dashboard-analytics-title" className="text-lg font-semibold tracking-tight text-slate-950">Performance trends</h2>
          <p className="text-sm text-slate-500">Comparative views of registrations, revenue, and account status</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <ChartCard
          title="New registrations"
          description="Provider and service-user signups by reporting period"
          icon={ChartColumn}
          iconClassName="text-blue-700"
          iconBackgroundClassName="bg-blue-50"
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={registrationsData} barGap={5} margin={{ top: 8, right: 12, left: -10, bottom: 0 }} accessibilityLayer>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="period" tick={AXIS_TICK_STYLE} axisLine={{ stroke: GRID_STROKE }} tickLine={false} />
              <YAxis tick={AXIS_TICK_STYLE} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#f8fafc' }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ color: '#475569', fontSize: 12, paddingTop: 12 }} />
              <Bar dataKey="Service Providers" fill={COLOR_BLUE} radius={[5, 5, 0, 0]} maxBarSize={42} />
              <Bar dataKey="Service Users" fill={COLOR_CYAN} radius={[5, 5, 0, 0]} maxBarSize={42} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Revenue by period"
          description="Recognized revenue across today, this week, and this month"
          icon={CircleDollarSign}
          iconClassName="text-emerald-700"
          iconBackgroundClassName="bg-emerald-50"
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData} margin={{ top: 8, right: 12, left: -4, bottom: 0 }} accessibilityLayer>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="period" tick={AXIS_TICK_STYLE} axisLine={{ stroke: GRID_STROKE }} tickLine={false} />
              <YAxis tick={AXIS_TICK_STYLE} axisLine={false} tickLine={false} width={64} tickFormatter={(value) => `$${Number(value).toLocaleString()}`} />
              <Tooltip formatter={(value: any) => formatCurrency(Number(value))} contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#f8fafc' }} />
              <Bar dataKey="revenue" name="Revenue" fill={COLOR_EMERALD} radius={[5, 5, 0, 0]} maxBarSize={68} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Payment status"
          description="Provider distribution across payment readiness states"
          icon={CreditCard}
          iconClassName="text-violet-700"
          iconBackgroundClassName="bg-violet-50"
        >
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={paymentStatusData} layout="vertical" margin={{ top: 4, right: 18, left: 8, bottom: 0 }} accessibilityLayer>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
              <XAxis type="number" tick={AXIS_TICK_STYLE} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="status" tick={AXIS_TICK_STYLE} axisLine={false} tickLine={false} width={126} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#f8fafc' }} />
              <Bar dataKey="count" name="Providers" radius={[0, 5, 5, 0]} maxBarSize={26}>
                {paymentStatusData.map((entry, index) => (
                  <Cell key={`payment-status-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Provider status"
          description="Where service providers sit in the onboarding lifecycle"
          icon={Settings2}
          iconClassName="text-amber-700"
          iconBackgroundClassName="bg-amber-50"
        >
          {providerStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={providerStatusData} layout="vertical" margin={{ top: 4, right: 18, left: 8, bottom: 0 }} accessibilityLayer>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                <XAxis type="number" tick={AXIS_TICK_STYLE} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="status" tick={AXIS_TICK_STYLE} axisLine={false} tickLine={false} width={126} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#f8fafc' }} />
                <Bar dataKey="count" name="Providers" radius={[0, 5, 5, 0]} maxBarSize={26}>
                  {providerStatusData.map((entry, index) => (
                    <Cell key={`provider-status-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart message="No provider status data yet" />
          )}
        </ChartCard>
      </div>
    </section>
  );
};
